import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import DataTable from "../../../components/ui/DataTable";
import Sidebar from "../../../components/layout/Sidebar";
import CustomPagination from "../../../components/ui/CustomPagination";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

function GplBookingsReport() {
  const { adminUser } = useAdminAuth();
  const [bookingData, setBookingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const itemsPerPage = 20;
  const paginate = (page) => setCurrentPage(page);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const paginationData = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/getbookingreport`, {
          params: { id: adminUser.user.id },
        });
        setBookingData(res.data || []);
      } catch (err) {
        console.error("Error fetching booking report data", err);
      }
    };

    fetchData();
  }, [adminUser.user.id]);

  // Options
  const branchOptions = [
    ...new Set(bookingData.map((d) => d.branchName)),
  ]
    .filter(Boolean)
    .map((b) => ({ label: b, value: b }));

  const employeeOptions = [
    ...new Set(bookingData.map((d) => d.username)),
  ]
    .filter(Boolean)
    .map((e) => ({ label: e, value: e }));

  // Filtering logic - use createdAt since bookingDate doesn't exist
  useEffect(() => {
    let filtered = bookingData;

    if (fromDate || toDate) {
      filtered = filtered.filter((item) => {
        const recordDate = new Date(item.createdAt);
        return (
          (!fromDate || new Date(fromDate) <= recordDate) &&
          (!toDate || new Date(toDate) >= recordDate)
        );
      });
    }

    if (selectedBranch) {
      filtered = filtered.filter((item) => item.branchName === selectedBranch.value);
    }

    if (selectedEmployee) {
      filtered = filtered.filter((item) => item.username === selectedEmployee.value);
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to page 1 on new filters
  }, [fromDate, toDate, selectedBranch, selectedEmployee, bookingData, searchTerm]);

  const columns = [
    { label: "S No", render: (_, i) => indexOfFirst + i + 1 },
    { label: "Region", render: (d) => d.regionName },
    { label: "Division", render: (d) => d.divisionName },
    { label: "Branch Name", render: (d) => d.branchName },
    { label: "Branch Code", render: (d) => d.branchCode },
    { label: "Application ID", render: (d) => d.ApplicationId },
    { label: "Customer ID", render: (d) => d.customerId },
    { label: "Title", render: (d) => d.title },
    { label: "Customer Name", render: (d) => d.memberName },
    { label: "Gender", render: (d) => d.gender },
    { label: "Date of Birth", render: (d) => d.dateOfBirth },
    { label: "Age", render: (d) => d.age },
    { label: "Phone Number", render: (d) => d.phoneNumber },
    { label: "Aadhar No", render: (d) => d.aadharNo },
    { label: "PAN No", render: (d) => d.panNo },
    {
      label: "Permanent Address",
      render: (d) => {
        const parts = [
          d.permanentDoorNo,
          d.permanentStreetNo,
          d.permanentPanchayat,
          d.permanentDistrict,
          d.permanentPincode
        ].filter(part => part && String(part).trim() !== "");
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    {
      label: "Current Address",
      render: (d) => {
        const parts = [
          d.currentDoorNo,
          d.currentStreetNo,
          d.currentPanchayat,
          d.currentDistrict,
          d.currentPincode
        ].filter(part => part && String(part).trim() !== "");
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    { label: "Loan Type", render: (d) => d.loanType },
    { label: "Employee Name", render: (d) => d.employeeName },
    {
      label: "Ornament Model No",
      render: (d) => d.ornamentModelNoByBm || d.proposedLoanDetails?.ornament_modelNo,
    },
    { label: "Ornament Name", render: (d) => d.ornamentNameByBm },

    { label: "Gold Rate", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.goldRate },
    {
      label: "Ornament Weight",
      render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.ornamentWeight || d.proposedLoanDetails?.ornament_wt,
    },
    { label: "Ornament Weight Value ", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.ornamentWeightValue },

    { label: "Wastage", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.wastage },
    { label: "Wastage Value", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.wastageValue },

    // { label: "Total", render: (d) => d.totalByBm },
    { label: "GST", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.gst },
    { label: "GST Value", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.gstValue },
    { label: "Grand Total", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.grandTotal },
    { label: "Booking Amount", render: (d) => d.fk_member_details_hasOne_booking_process_bm_memberId?.bookingAmount },
    { label: "Booking Date", render: (d) => d.createdAt ? new Date(d.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-" },

    // { label: "EMI Date", render: (d) => d.receiptEmiDate },
    // { label: "EMI Amount", render: (d) => d.receiptEmiAmount },
    // { label: "Received Amount", render: (d) => d.receivedAmount },
    // { label: "Receipt Description", render: (d) => d.receiptDescription },
    // { label: "Collected Date", render: (d) => d.collectedDate },
    // { label: "Loan Status", render: (d) => d.loanStatus },
    // { label: "Processing Charge", render: (d) => d.processingCharge },
    // { label: "Insurance Amount", render: (d) => d.insuranceAmount },
    // {
    //   label: "Sanctioned Loan Amount",
    //   render: (d) => d.sanctionedLoanAmountBySanctionCommittee,
    // },
    // {
    //   label: "Business Type",
    //   render: (d) => d.businessDetails?.businessType,
    // },
    // {
    //   label: "Nature of Business",
    //   render: (d) => d.businessDetails?.natureOfBusiness,
    // },
    // {
    //   label: "Monthly Business Income",
    //   render: (d) => d.businessDetails?.monthlyBusinessIncome,
    // },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">GPL Booking Report</h1>
            <div className="w-100">
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>

                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranch}
                    onChange={(val) => {
                      setSelectedBranch(val);
                      if (fromDate && toDate && val && selectedEmployee) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployee}
                    onChange={(val) => {
                      setSelectedEmployee(val);
                      if (fromDate && toDate && selectedBranch && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="GplBookingsReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GplBookingsReport;
